import type { AppWavePin, AppWaveTags, Version } from '@keboola/api-client/developerPortal/types';

/** Waves the stacks run on: 1 and 2 are multi-tenant stacks, 3 is every single-tenant stack. */
const ROLLOUT_WAVES = [1, 2, 3] as const;

/** Pins by wave number. */
export type WavePins = ReadonlyMap<number, AppWavePin>;

const isPin = (value: unknown): value is AppWavePin =>
  typeof value === 'object' &&
  value !== null &&
  'tag' in value &&
  typeof value.tag === 'string' &&
  'version' in value &&
  typeof value.version === 'number';

/** The pins in a `waveTags` value; a missing, empty or malformed value reads as no pins. */
export const readPins = (waveTags: unknown): WavePins => {
  const pins = new Map<number, AppWavePin>();
  if (typeof waveTags !== 'object' || waveTags === null) return pins;
  for (const [key, pin] of Object.entries(waveTags)) {
    const wave = Number(key);
    if (Number.isInteger(wave) && wave >= 1 && wave <= 9 && isPin(pin)) pins.set(wave, pin);
  }
  return pins;
};

/** The `waveTags` body for these pins; no pins is `null`, which clears them. */
export const toWaveTags = (pins: WavePins): AppWaveTags | null =>
  pins.size === 0
    ? null
    : Object.fromEntries([...pins].sort(([a], [b]) => a - b).map(([wave, pin]) => [wave, pin]));

/** Whether two `waveTags` values hold the same pins, whatever their key order. */
export const samePins = (a: unknown, b: unknown): boolean => {
  const key = (waveTags: unknown) =>
    [...readPins(waveTags)]
      .sort(([x], [y]) => x - y)
      .map(([wave, pin]) => `${wave}:${pin.version}:${pin.tag}`)
      .join(',');
  return key(a) === key(b);
};

/** The waves to show: every wave the stacks use, plus any other wave that has a pin. */
export const shownWaves = (pins: WavePins): number[] =>
  [...new Set([...ROLLOUT_WAVES, ...pins.keys()])].sort((a, b) => a - b);

/**
 * What a stack on `wave` runs, as apps-api resolves it.
 *
 * 1. The pin of the largest pinned wave not above `wave`, and that wave.
 * 2. `null` when no such pin exists: the stack runs the latest version.
 */
export const resolveWave = (
  pins: WavePins,
  wave: number,
): { pin: AppWavePin; fromWave: number } | null => {
  const fromWave = Math.max(...[...pins.keys()].filter((key) => key <= wave), 0);
  const pin = pins.get(fromWave);
  return pin === undefined ? null : { pin, fromWave };
};

/**
 * What a stack on `wave` runs, in words.
 *
 * - `ignored`: apps-api skips the pin the wave resolves to, so the stack runs the latest version.
 */
export const describeWave = (pins: WavePins, wave: number, ignored = false): string => {
  const resolved = resolveWave(pins, wave);
  if (resolved === null) return 'The latest version';
  const { pin, fromWave } = resolved;
  const runs = `${pin.tag} (version ${pin.version})`;
  if (ignored) {
    return `The latest version: apps-api ignores the wave ${fromWave} pin, ${runs}, because it no longer matches the current image`;
  }
  return fromWave === wave ? runs : `${runs}, from the wave ${fromWave} pin`;
};

/** The pins apps-api ignores: none of `candidates` has their version and tag. */
export const ignoredPins = (pins: WavePins, candidates: readonly AppWavePin[]): WavePins =>
  new Map(
    [...pins].filter(
      ([, pin]) =>
        !candidates.some(
          (candidate) => candidate.version === pin.version && candidate.tag === pin.tag,
        ),
    ),
  );

/** The pins a rollback to `target` writes: a pin newer than the target moves to it; the rest stay. */
export const pinsAfterRollback = (pins: WavePins, target: AppWavePin): WavePins =>
  new Map([...pins].map(([wave, pin]) => [wave, pin.version > target.version ? target : pin]));

const readString = (value: unknown, key: string): string | null => {
  if (typeof value !== 'object' || value === null) return null;
  const found: unknown = Object.entries(value).find(([name]) => name === key)?.[1];
  return typeof found === 'string' ? found : null;
};

/** The pin naming `version`, or `null` when it has no tag or number to pin. */
export const pinOf = (version: Version): AppWavePin | null => {
  const tag = readString(version.repository, 'tag');
  return tag !== null && version.version !== undefined ? { tag, version: version.version } : null;
};

/**
 * The pins an editor selection names.
 *
 * - `selection` maps a wave to a version number; `''` is no pin.
 * - A version absent from `candidates` is dropped, so it can never be sent without its tag.
 */
export const pinsFromSelection = (
  selection: Record<string, string>,
  candidates: readonly AppWavePin[],
): WavePins => {
  const pins = new Map<number, AppWavePin>();
  for (const [wave, version] of Object.entries(selection)) {
    const pin = candidates.find((candidate) => String(candidate.version) === version);
    if (version !== '' && pin !== undefined) pins.set(Number(wave), pin);
  }
  return pins;
};

/** Versions apps-api accepts as a pin: built from `repositoryUri`, the image the app has now. */
export const pinnableVersions = (versions: Version[], repositoryUri: string | null): Version[] =>
  versions.filter(
    (version) =>
      pinOf(version) !== null &&
      repositoryUri !== null &&
      readString(version.repository, 'uri') === repositoryUri,
  );
